<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <!-- Background overlay -->
          <div class="modal-overlay" @click="closeModal"></div>

          <!-- Modal panel -->
          <div class="inline-block align-bottom bg-white dark:bg-surface-900 rounded-2xl text-left overflow-hidden shadow-elevation-3 dark:shadow-glass-lg transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-surface-200/60 dark:border-surface-800/60 animate-scale-in">
            <div class="p-6">
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0 flex items-center justify-center h-11 w-11 rounded-xl bg-accent-50 dark:bg-accent-500/10">
                  <DocumentArrowDownIcon class="h-5 w-5 text-accent-600 dark:text-accent-400" />
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-semibold text-surface-900 dark:text-surface-100" id="modal-title">
                    Export Summary Report
                  </h3>
                  <p class="text-sm text-surface-500 dark:text-surface-400 mt-1">
                    Choose which customer accounts to include in the report:
                  </p>

                  <!-- Options -->
                  <div class="mt-5 space-y-3">
                    <label
                      class="relative flex items-start p-4 border rounded-xl cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800/40 transition-all duration-200"
                      :class="selectedOption === 'current' ? 'border-accent-500 bg-accent-50/50 dark:bg-accent-500/5 dark:border-accent-500/40' : 'border-surface-200 dark:border-surface-700'"
                    >
                      <input type="radio" v-model="selectedOption" value="current" class="sr-only" />
                      <div class="flex items-center h-5">
                        <div
                          class="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors"
                          :class="selectedOption === 'current' ? 'border-accent-500' : 'border-surface-300 dark:border-surface-600'"
                        >
                          <div v-if="selectedOption === 'current'" class="w-2 h-2 rounded-full bg-accent-500"></div>
                        </div>
                      </div>
                      <div class="ml-3 flex-1">
                        <span class="block text-sm font-semibold text-surface-800 dark:text-surface-100">
                          Current Customer Only
                        </span>
                        <span class="block text-sm text-surface-500 dark:text-surface-400 mt-0.5">
                          {{ currentCustomerName }}
                        </span>
                      </div>
                    </label>

                    <label
                      v-if="hasMultipleCustomers"
                      class="relative flex items-start p-4 border rounded-xl cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800/40 transition-all duration-200"
                      :class="selectedOption === 'all' ? 'border-accent-500 bg-accent-50/50 dark:bg-accent-500/5 dark:border-accent-500/40' : 'border-surface-200 dark:border-surface-700'"
                    >
                      <input type="radio" v-model="selectedOption" value="all" class="sr-only" />
                      <div class="flex items-center h-5">
                        <div
                          class="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors"
                          :class="selectedOption === 'all' ? 'border-accent-500' : 'border-surface-300 dark:border-surface-600'"
                        >
                          <div v-if="selectedOption === 'all'" class="w-2 h-2 rounded-full bg-accent-500"></div>
                        </div>
                      </div>
                      <div class="ml-3 flex-1">
                        <span class="block text-sm font-semibold text-surface-800 dark:text-surface-100">
                          All Customer Accounts
                        </span>
                        <span class="block text-sm text-surface-500 dark:text-surface-400 mt-0.5">
                          Comprehensive report for all {{ totalCustomers }} accounts
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="px-6 py-4 border-t border-surface-200/60 dark:border-surface-800/60 bg-surface-50/50 dark:bg-surface-900/50 flex justify-end gap-3">
              <BaseButton variant="secondary" @click="closeModal" :disabled="isExporting">
                Cancel
              </BaseButton>
              <BaseButton variant="primary" @click="handleExport" :loading="isExporting" :icon="DocumentArrowDownIcon">
                Export PDF
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref } from 'vue'
import { DocumentArrowDownIcon } from '@heroicons/vue/24/outline'
import BaseButton from '@/components/ui/BaseButton.vue'

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  currentCustomerName: { type: String, required: true },
  totalCustomers: { type: Number, required: true },
  hasMultipleCustomers: { type: Boolean, default: false },
  isExporting: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'export'])

const selectedOption = ref('current')

const closeModal = () => {
  if (!props.isExporting) {
    emit('update:modelValue', false)
  }
}

const handleExport = () => {
  emit('export', selectedOption.value)
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
